import { useEffect, useRef, useState } from 'react';
import { apiStreamPost, ApiError, apiPost } from '../api/client';
import { WaitingMascotArena } from '../components/WaitingMascotArena';
import type { DraftCard, AuthUser } from '../types';

interface WishInputProps {
  user: AuthUser;
  // We deliberately no longer expose k2.6 reasoning_content to the client (prompt-leak
  // protection). The third arg is kept for backward compat but is now always empty.
  onCardsReady: (wish: string, cards: DraftCard[], reasoning: string) => void;
  onLogout: () => void;
  onPendingChange?: (pending: boolean) => void;
  /** Only present when the user came here from the Desk; absent on first-time onboarding. */
  onBackToDesk?: () => void;
}

interface DeckResponse {
  ok: boolean;
  adapter: string;
  data: { cards: DraftCard[] };
}

const EXAMPLE_WISH = `发布一篇公众号、小红书、微信图文；优化迭代每日情报看板加入 GitHub Trending；规划 CyberUnion 第一版的功能范围；准备明天公司例会的 PPT。`;

// 60-150 秒等待时间是传达产品理念的最大窗口。每条 6 秒,8 条 ≈ 48 秒一圈,
// 大概率用户能看到 4-6 条。顺序经过设计:先讲"不做什么",再讲"怎么帮你",最后落到创作者身份。
const VALUE_PROPS = [
  '不打卡、不评分、不奖励、不羞辱。这是一张安静的桌面,不是任务管理器。',
  '桌面最多只放三张卡。不是少,是恰好——注意力本来就只适合同时碰几件事。',
  '5 分钟不是考试,只是一个入口。能回来,比硬撑更重要。',
  '不要求你写完整的 to-do。只问一句:今天想推进什么。',
  '想调一下说一声,入口会变更小,直到你能从一张空文档开始。',
  '没选中的卡不会消失,只是先不占用这一段。线头不是债务。',
  '每一轮 5 分钟,只记一句"停在哪"。下次回来不用从零开始理解。',
  '来自一个也常常启动困难的创作者。',
];
const VALUE_PROP_INTERVAL_MS = 6000;
const EXPECTED_DURATION_S = 150; // for progress bar — cap at 95% until result lands

export function WishInput({
  user,
  onCardsReady,
  onLogout,
  onPendingChange,
  onBackToDesk,
}: WishInputProps) {
  const [wish, setWish] = useState('');
  // S06/S07: 'auto' = 让它自动拆 2-7 张；'single' = 只要一张最该先启动的卡。
  const [cardMode, setCardMode] = useState<'auto' | 'single'>('auto');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Most recent narration line from the server (e.g. "正在把这段话切成几段独立的事...").
  // We typewriter-animate this on screen — feels like real LLM streaming but is actually
  // a curated product-narration pool (zero prompt leak risk).
  const [latestStep, setLatestStep] = useState<string>('');
  const [typedLine, setTypedLine] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [propIndex, setPropIndex] = useState(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    onPendingChange?.(pending);
    return () => {
      if (pending) onPendingChange?.(false);
    };
  }, [pending, onPendingChange]);

  // Tick a 1s elapsed counter while pending so the user can see time passing.
  useEffect(() => {
    if (!pending) return;
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [pending]);

  // Rotate value propositions every VALUE_PROP_INTERVAL_MS while pending.
  useEffect(() => {
    if (!pending) return;
    setPropIndex(0);
    const id = window.setInterval(() => {
      setPropIndex((i) => (i + 1) % VALUE_PROPS.length);
    }, VALUE_PROP_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [pending]);

  // Typewriter the latest narration line — feels like real token streaming.
  useEffect(() => {
    if (!latestStep) {
      setTypedLine('');
      return;
    }
    setTypedLine('');
    let i = 0;
    const id = window.setInterval(() => {
      i++;
      if (i > latestStep.length) {
        window.clearInterval(id);
        return;
      }
      setTypedLine(latestStep.slice(0, i));
    }, 70);
    return () => window.clearInterval(id);
  }, [latestStep]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!wish.trim() || pending) return;
    setPending(true);
    setError(null);
    setLatestStep('');
    setTypedLine('');
    setElapsed(0);
    startRef.current = Date.now();

    const trimmed = wish.trim();
    let result: DeckResponse | null = null;
    let errorMsg: string | null = null;
    let streamErr = false;
    let streamErrMessage = ''; // captured to disambiguate network vs content errors

    try {
      await apiStreamPost(
        '/api/agent/deck/stream',
        { wish: trimmed, poolPreview: [], cardCountHint: cardMode },
        (ev) => {
          if (ev.event === 'progress') {
            try {
              const obj = JSON.parse(ev.data) as { text?: string };
              if (obj.text) setLatestStep(obj.text);
            } catch { /* skip malformed */ }
          } else if (ev.event === 'result') {
            try {
              result = JSON.parse(ev.data) as DeckResponse;
            } catch { /* skip */ }
          } else if (ev.event === 'error') {
            try {
              const obj = JSON.parse(ev.data) as { message?: string };
              console.warn('[deck/stream error]', obj.message);
              streamErrMessage = obj.message ?? '';
            } catch {
              console.warn('[deck/stream error] (unparseable)');
            }
            streamErr = true;
          }
        },
      );
    } catch (err) {
      if (err instanceof ApiError) {
        console.warn('[deck/stream transport]', err.status);
        streamErrMessage = `transport ${err.status}`;
      } else if (err instanceof Error) {
        streamErrMessage = err.message;
      }
      streamErr = true;
    }

    // If streaming didn't deliver a result, fall back to non-stream /api/agent/deck
    // which uses moonshot-v1-32k (fast + reliable) — but skip the visual progress noise.
    if (!result || streamErr) {
      setLatestStep('换一条通道再摆一次桌面...');
      try {
        const data = await apiPost<DeckResponse>('/api/agent/deck', {
          wish: trimmed,
          poolPreview: [],
          cardCountHint: cardMode,
        });
        result = data;
        errorMsg = null;
      } catch (fallbackErr) {
        // Pick error message based on what actually failed.
        const combinedErr = streamErrMessage + ' ' + String(fallbackErr);
        const isNetwork = /Network|aborted|timeout|connection|fetch|502|503|504/i.test(combinedErr);
        errorMsg = isNetwork
          ? '网络刚才没拼上,等一下再试一次就好。'
          : '这次没有摆出合适的卡。你写的这段话没有丢,可以稍后再试,或者换一种更口语的说法。';
      }
    }

    if (errorMsg) {
      setError(errorMsg);
      setPending(false);
      return;
    }
    if (result) {
      // reasoning intentionally empty — never leaves the server.
      onCardsReady(trimmed, result.data.cards, '');
      return;
    }
    setError('这次没有摆出卡。你写的这段话没有丢,稍后再试一次就好。');
    setPending(false);
  }

  if (pending) {
    const pct = Math.min(95, Math.floor((elapsed / EXPECTED_DURATION_S) * 100));
    return (
      <section className="panel wish thinking-panel">
        <span className="tiny-label">
          <i className="dot cyan pulse" /> 正在摆桌面 · {elapsed}s
        </span>
        <h1>正在把这段话翻译成几张可以碰的卡…</h1>

        {/* Value-prop rotator — the 60-150s window is our biggest chance to communicate
            why this product exists, not what API call we're running. */}
        <div
          className="value-prop-rotator"
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          <WaitingMascotArena />
          <p className="value-prop" key={propIndex}>
            {VALUE_PROPS[propIndex]}
          </p>
        </div>

        {/* Thin progress bar estimated against EXPECTED_DURATION_S. Caps at 95% so the
            jump-to-100 only happens when the actual result arrives. */}
        <div className="progress-bar" aria-label={`已用 ${elapsed} 秒,预估 60-150 秒`}>
          <div className="progress-fill" style={{ width: `${pct}%` }} />
        </div>

        {/* Single-line typewriter narration — feels like real LLM streaming but content
            is curated product narration, never raw reasoning_content. */}
        {latestStep && (
          <p
            className="thinking-line"
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <span className="thinking-prompt">›</span>
            <span className="thinking-text">{typedLine}</span>
            <span className="thinking-caret" aria-hidden="true" />
          </p>
        )}
      </section>
    );
  }

  const isDevStub = user.email === 'dev@local.test';
  return (
    <section className="panel wish wish-input">
      <div className="user-strip">
        <span className="muted tiny">
          {isDevStub ? 'DEV 模式 · 已跳过登录' : `已登录 ${user.email}`}
        </span>
        <div className="user-strip-right">
          {onBackToDesk && (
            <button className="link-subtle" type="button" onClick={onBackToDesk}>
              ← 回桌面
            </button>
          )}
          {!isDevStub && (
            <button className="link-subtle" type="button" onClick={onLogout}>
              退出登录
            </button>
          )}
        </div>
      </div>

      <span className="tiny-label">
        <i className="dot cyan" /> 写下愿望
      </span>
      <h1>把愿望写成便签</h1>
      <p className="muted">放到桌面上,5 分钟启动一张。写一团乱乱的话也行,不用先排优先级。</p>
      <form onSubmit={submit}>
        <textarea
          className="text-input textarea"
          value={wish}
          onChange={(e) => setWish(e.target.value)}
          placeholder={EXAMPLE_WISH}
          rows={6}
          autoFocus
          maxLength={2000}
        />
        <div className="actions">
          <span className="muted tiny">想要几张卡:</span>
          <button
            className="link-subtle"
            type="button"
            aria-pressed={cardMode === 'auto'}
            onClick={() => setCardMode('auto')}
          >
            {cardMode === 'auto' ? '● 自动(多张)' : '○ 自动(多张)'}
          </button>
          <button
            className="link-subtle"
            type="button"
            aria-pressed={cardMode === 'single'}
            onClick={() => setCardMode('single')}
          >
            {cardMode === 'single' ? '● 只要一张' : '○ 只要一张'}
          </button>
        </div>
        <div className="actions actions-between">
          <button
            className="link-subtle"
            type="button"
            onClick={() => setWish(EXAMPLE_WISH)}
          >
            用一个示例
          </button>
          <button className="btn-lime" type="submit" disabled={!wish.trim()}>
            写成便签
          </button>
        </div>
      </form>
      {error && (
        <p className="error-line">
          <i className="dot amber" /> {error}
        </p>
      )}
    </section>
  );
}
